import React from "react";
import { Instagram, ExternalLink, Globe } from "lucide-react";
import { CEF_LINKS, CEF_HANDLES } from "@/lib/cef-links";
import { useAuth } from "@/lib/auth-context";

const TikTokIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.27a8.16 8.16 0 0 0 4.77 1.52V7.4a4.85 4.85 0 0 1-1.84-.71z" /></svg>
);
const WhatsAppIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.86 9.86 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.84 9.84 0 0 0 12.04 2Zm5.83 14.04c-.25.7-1.43 1.33-2.01 1.42-.51.07-1.18.11-1.9-.12-.44-.14-1-.32-1.72-.63-3.03-1.31-5-4.36-5.16-4.56-.15-.2-1.22-1.62-1.22-3.09 0-1.47.77-2.19 1.04-2.49.27-.3.59-.37.79-.37l.57.01c.18.01.43-.07.67.51.25.6.84 2.07.92 2.22.07.15.12.32.02.52-.1.2-.15.32-.3.5-.15.18-.32.4-.45.54-.15.15-.31.31-.13.62.18.32.81 1.33 1.74 2.16 1.2 1.07 2.21 1.4 2.52 1.55.32.15.51.13.7-.08.2-.22.81-.94 1.03-1.27.22-.32.45-.27.74-.16.3.11 1.88.89 2.2 1.05.32.16.54.24.62.37.07.13.07.78-.18 1.48Z"/></svg>
);

const POSTS = [
  { platform: "instagram", title: `Marche FWB en images — ${CEF_HANDLES.instagram}`, thumb: "https://images.pexels.com/photos/6217826/pexels-photo-6217826.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", url: CEF_LINKS.instagram },
  { platform: "tiktok", title: `5 droits que tu ignores — ${CEF_HANDLES.tiktok}`, thumb: "https://images.pexels.com/photos/8199185/pexels-photo-8199185.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", url: CEF_LINKS.tiktok },
  { platform: "whatsapp", title: "Rejoins le canal WhatsApp du CEF", thumb: "https://images.unsplash.com/photo-1626383530747-3d47409f68b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxkaXZlcnNlJTIwdGVlbmFnZXJzJTIwc2Nob29sJTIwc3R1ZGVudHMlMjB0YWxraW5nJTIwdG9nZXRoZXJ8ZW58MHx8fHwxNzgwMjU5MjU2fDA&ixlib=rb-4.1.0&q=85", url: CEF_LINKS.whatsapp_channel },
  { platform: "website", title: "Site officiel — lecef.org", thumb: "https://images.unsplash.com/photo-1554830448-6cd6318a2f55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxzdHVkZW50JTIwcHJvdGVzdCUyMGFjdGl2aXNtJTIweW91bmclMjB0ZWVuYWdlcnMlMjBob2xkaW5nJTIwc2lnbnN8ZW58MHx8fHwxNzgwMjU5MjU3fDA&ixlib=rb-4.1.0&q=85", url: CEF_LINKS.website },
];

function PlatformIcon({ platform, className }) {
  if (platform === "instagram") return <Instagram className={className} />;
  if (platform === "tiktok") return <TikTokIcon className={className} />;
  if (platform === "whatsapp") return <WhatsAppIcon className={className} />;
  return <Globe className={className} />;
}

const COLOR = {
  instagram: "bg-cef-pink text-white",
  tiktok: "bg-black text-white",
  whatsapp: "bg-cef-green",
  website: "bg-cef-blue text-white",
};

export default function SocialFeed() {
  const { user } = useAuth();
  const posts = POSTS.filter((p) => p.platform !== "whatsapp" || user);
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <h2 className="font-display font-black text-2xl sm:text-4xl tracking-tighter uppercase">
          Suis-nous sur les <span className="bg-cef-pink text-white px-2 border-2 border-black inline-block -rotate-1">réseaux</span>
        </h2>
        <div className="flex gap-2 flex-wrap">
          <a href={CEF_LINKS.instagram} target="_blank" rel="noreferrer" className="neo-btn bg-cef-pink text-white text-sm py-2 px-3" data-testid="follow-instagram">
            <Instagram className="w-4 h-4" /> {CEF_HANDLES.instagram}
          </a>
          <a href={CEF_LINKS.tiktok} target="_blank" rel="noreferrer" className="neo-btn bg-black text-white text-sm py-2 px-3" data-testid="follow-tiktok">
            <TikTokIcon className="w-4 h-4" /> {CEF_HANDLES.tiktok}
          </a>
          {user && (
            <a href={CEF_LINKS.whatsapp_channel} target="_blank" rel="noreferrer" className="neo-btn bg-cef-green text-sm py-2 px-3" data-testid="follow-whatsapp">
              <WhatsAppIcon className="w-4 h-4" /> WhatsApp
            </a>
          )}
          <a href={CEF_LINKS.website} target="_blank" rel="noreferrer" className="neo-btn bg-cef-blue text-white text-sm py-2 px-3" data-testid="follow-website">
            <Globe className="w-4 h-4" /> lecef.org
          </a>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="social-feed">
        {posts.map((p) => (
          <a key={p.url + p.title} href={p.url} target="_blank" rel="noreferrer" className="neo-card overflow-hidden block group" data-testid={`social-post-${p.platform}`}>
            <div className="relative aspect-square overflow-hidden border-b-2 border-black">
              <img src={p.thumb} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition" />
              <div className={`absolute top-2 left-2 w-9 h-9 inline-flex items-center justify-center border-2 border-black rounded-md shadow-brutal ${COLOR[p.platform]}`}>
                <PlatformIcon platform={p.platform} className="w-4 h-4" />
              </div>
            </div>
            <div className="p-3 flex items-center justify-between">
              <span className="text-sm font-bold line-clamp-1">{p.title}</span>
              <ExternalLink className="w-3.5 h-3.5 shrink-0" />
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

